// Device / session tracking (best-effort, purely additive).
//
// Sends this browser's device metadata to the backend after login and keeps the
// login-created session marked "active" via a lightweight heartbeat. There is no
// device-management UI here — just register + heartbeat.
//
// HARD RULE: every call is best-effort. If the network is slow or the endpoints
// are unavailable, nothing here may block or break an existing flow — all calls
// are wrapped in try/catch and fail silently.
//
// Token: always the CURRENTLY ACTIVE token via getToken() (localStorage "token"),
// which the app swaps during manager impersonation — so heartbeat/register follow
// whichever account is active.
import { axios, publicAxios, endpoints, getToken } from 'Api';

const DEVICE_ID_KEY = 'deviceId';
const HEARTBEAT_INTERVAL_MS = 3 * 60 * 1000; // ~3 minutes

// Stable per-browser id: created once with crypto.randomUUID() and reused forever
// from localStorage. Falls back to a random string if crypto is unavailable, and
// to a constant if storage is blocked (so callers always get a usable value).
export const getDeviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = (window.crypto && typeof window.crypto.randomUUID === 'function')
        ? window.crypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return 'web';
  }
};

// Friendly "Chrome on macOS"-style label from the user agent. Purely cosmetic;
// any parse failure degrades to a generic "Web".
const getDeviceName = () => {
  try {
    const ua = navigator.userAgent || '';
    let browser = 'Browser';
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/OPR\//.test(ua) || /Opera/.test(ua)) browser = 'Opera';
    else if (/Chrome\//.test(ua)) browser = 'Chrome';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua)) browser = 'Safari';

    let os = 'Unknown OS';
    if (/Windows/.test(ua)) os = 'Windows';
    else if (/Mac OS X|Macintosh/.test(ua)) os = 'macOS';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
    else if (/Linux/.test(ua)) os = 'Linux';

    return `${browser} on ${os}`;
  } catch (e) {
    return 'Web';
  }
};

// Web build/version, from the CRA build env if present, else a stable fallback.
const getAppVersion = () => {
  try {
    return process.env.REACT_APP_VERSION || 'web';
  } catch (e) {
    return 'web';
  }
};

// POST device metadata for THIS session (created by login) and mark it active.
// Skips silently when there's no active token.
export const registerDevice = async () => {
  try {
    const token = getToken();
    if (!token) return;
    await axios.post(endpoints.registerDevice, {
      token,
      device_id: getDeviceId(),
      device_type: 'WEB',
      platform: 'WEB',
      device_name: getDeviceName(),
      app_version: getAppVersion(),
    });
  } catch (e) {
    // best-effort: never surface errors
  }
};

// Refresh "last active" for this session. Fire-and-forget; skips without a token.
export const sendHeartbeat = async () => {
  try {
    const token = getToken();
    if (!token) return;
    await axios.post(endpoints.deviceHeartbeat, { token });
  } catch (e) {
    // best-effort: never surface errors
  }
};

// ── Heartbeat lifecycle ────────────────────────────────────────────────────
// Interval ticks every ~3 min but only actually beats when a token exists AND
// the tab is visible (paused while hidden). Also beats on window focus and on
// visibility→visible so a returning tab refreshes immediately.
let heartbeatTimer = null;
let listenersBound = false;

const tabVisible = () => (typeof document === 'undefined'
  ? true
  : document.visibilityState !== 'hidden');

const heartbeatTick = () => {
  try {
    if (getToken() && tabVisible()) sendHeartbeat();
  } catch (e) {
    // ignore
  }
};

const onFocus = () => heartbeatTick();
const onVisibilityChange = () => { if (tabVisible()) heartbeatTick(); };

// Idempotent: safe to call repeatedly (e.g. on every sign-in) without stacking
// intervals or listeners.
export const startDeviceHeartbeat = () => {
  try {
    if (heartbeatTimer) return;
    heartbeatTimer = setInterval(heartbeatTick, HEARTBEAT_INTERVAL_MS);
    if (!listenersBound) {
      window.addEventListener('focus', onFocus);
      document.addEventListener('visibilitychange', onVisibilityChange);
      listenersBound = true;
    }
    // Beat once now if we're foregrounded and logged in.
    heartbeatTick();
  } catch (e) {
    // ignore
  }
};

// Stop the interval and detach listeners — call on logout / when signed out.
export const stopDeviceHeartbeat = () => {
  try {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    if (listenersBound) {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      listenersBound = false;
    }
  } catch (e) {
    // ignore
  }
};

// Revoke THIS session on the server as part of logout, and stop the heartbeat.
// Fire-and-forget so it never blocks sign-out. Logout clears the token right
// after this returns, so we snapshot the token and use publicAxios (which has no
// token interceptor) — otherwise the interceptor would overwrite the body's
// token with the already-cleared getToken() value.
export const revokeCurrentDevice = () => {
  try {
    const token = getToken();
    stopDeviceHeartbeat();
    if (!token) return;
    publicAxios.post(endpoints.revokeCurrentDevice, { token }).catch(() => {});
  } catch (e) {
    // best-effort: never block or surface errors on logout
  }
};
